/**
 * Rate Limiter Utility
 * Prevents brute force attacks on authentication endpoints
 * Tracks attempts by email/IP and implements cooldown periods
 */

interface RateLimitData {
  attempts: number;
  lastAttempt: number;
  blockedUntil?: number;
}

const STORAGE_KEY = 'auth_rate_limit';

// Configuration
const MAX_ATTEMPTS = 5; // Maximum attempts before cooldown
const COOLDOWN_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds
const ATTEMPT_WINDOW = 5 * 60 * 1000; // 5 minute window for counting attempts
const LONG_COOLDOWN_ATTEMPTS = 10; // Attempts before longer cooldown
const LONG_COOLDOWN_DURATION = 60 * 60 * 1000; // 1 hour

/**
 * Get rate limit data from localStorage
 */
function getRateLimitData(): Map<string, RateLimitData> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return new Map();

    const data = JSON.parse(stored);
    return new Map(Object.entries(data));
  } catch (error) {
    console.error('Error reading rate limit data:', error);
    return new Map();
  }
}

/**
 * Save rate limit data to localStorage
 */
function saveRateLimitData(data: Map<string, RateLimitData>): void {
  try {
    const obj = Object.fromEntries(data);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch (error) {
    console.error('Error saving rate limit data:', error);
  }
}

/**
 * Clean up old rate limit entries
 */
function cleanupOldEntries(data: Map<string, RateLimitData>): Map<string, RateLimitData> {
  const now = Date.now();
  const cleaned = new Map<string, RateLimitData>();

  // Convert iterator to array to avoid downlevelIteration requirement
  Array.from(data.entries()).forEach(([key, value]) => {
    // Remove entries older than 1 hour that aren't blocked
    if (!value.blockedUntil && now - value.lastAttempt < 60 * 60 * 1000) {
      cleaned.set(key, value);
    }
    // Keep blocked entries until they expire
    if (value.blockedUntil && value.blockedUntil > now) {
      cleaned.set(key, value);
    }
  });

  return cleaned;
}

/**
 * Check if an email/identifier is currently rate limited
 * @param identifier - Usually email address or IP
 * @returns Object with isBlocked status and time until unblock
 */
export function checkRateLimit(identifier: string): {
  isBlocked: boolean;
  remainingTime?: number;
  attemptsLeft?: number;
} {
  const data = getRateLimitData();
  const now = Date.now();
  const limitData = data.get(identifier.toLowerCase());

  if (!limitData) {
    return { isBlocked: false, attemptsLeft: MAX_ATTEMPTS };
  }

  // Check if currently blocked
  if (limitData.blockedUntil && limitData.blockedUntil > now) {
    return {
      isBlocked: true,
      remainingTime: Math.ceil((limitData.blockedUntil - now) / 1000), // seconds
    };
  }

  // Check if attempts are within the time window
  if (now - limitData.lastAttempt > ATTEMPT_WINDOW) {
    // Reset attempts if outside time window
    return { isBlocked: false, attemptsLeft: MAX_ATTEMPTS };
  }

  // Calculate remaining attempts
  const attemptsLeft = Math.max(0, MAX_ATTEMPTS - limitData.attempts);

  return {
    isBlocked: false,
    attemptsLeft,
  };
}

/**
 * Record a failed authentication attempt
 * @param identifier - Usually email address or IP
 */
export function recordFailedAttempt(identifier: string): void {
  const data = getRateLimitData();
  const now = Date.now();
  const key = identifier.toLowerCase();
  const existing = data.get(key);

  if (!existing) {
    // First attempt
    data.set(key, {
      attempts: 1,
      lastAttempt: now,
    });
  } else if (now - existing.lastAttempt > ATTEMPT_WINDOW) {
    // Outside time window, reset
    data.set(key, {
      attempts: 1,
      lastAttempt: now,
    });
  } else {
    // Increment attempts
    const newAttempts = existing.attempts + 1;
    const newData: RateLimitData = {
      attempts: newAttempts,
      lastAttempt: now,
    };

    // Apply cooldown if exceeded max attempts
    if (newAttempts >= LONG_COOLDOWN_ATTEMPTS) {
      newData.blockedUntil = now + LONG_COOLDOWN_DURATION;
    } else if (newAttempts >= MAX_ATTEMPTS) {
      newData.blockedUntil = now + COOLDOWN_DURATION;
    }

    data.set(key, newData);
  }

  // Clean up and save
  const cleaned = cleanupOldEntries(data);
  saveRateLimitData(cleaned);
}

/**
 * Record a successful authentication (clears the rate limit for this identifier)
 * @param identifier - Usually email address or IP
 */
export function recordSuccessfulAttempt(identifier: string): void {
  const data = getRateLimitData();
  const key = identifier.toLowerCase();

  // Remove rate limit data on successful login
  data.delete(key);

  const cleaned = cleanupOldEntries(data);
  saveRateLimitData(cleaned);
}

/**
 * Manually clear rate limit for an identifier (admin/support use)
 * @param identifier - Usually email address or IP
 */
export function clearRateLimit(identifier: string): void {
  const data = getRateLimitData();
  data.delete(identifier.toLowerCase());
  saveRateLimitData(data);
}

/**
 * Get human-readable time remaining message
 * @param seconds - Seconds remaining
 */
export function getRateLimitMessage(seconds: number): string {
  if (seconds < 60) {
    return `Too many attempts. Please try again in ${seconds} second${seconds !== 1 ? 's' : ''}.`;
  }

  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) {
    return `Too many attempts. Please try again in ${minutes} minute${minutes !== 1 ? 's' : ''}.`;
  }

  const hours = Math.ceil(minutes / 60);
  return `Too many attempts. Please try again in ${hours} hour${hours !== 1 ? 's' : ''}.`;
}

/**
 * Clear all rate limit data (for testing/development)
 */
export function clearAllRateLimits(): void {
  localStorage.removeItem(STORAGE_KEY);
}
